import axiosLib, { AxiosError as _AxiosError, AxiosResponse as _AxiosResponse } from 'axios'
import _ from 'lodash'
import store from '../services/store'

export type AxiosResponse = _AxiosResponse //| undefined
export type AxiosError = _AxiosError<any, any>
export type AxiosSuccessCallback = (response: AxiosResponse) => void
export type AxiosErrorCallback = (response: AxiosError) => void
export const AXIOS_ERR_NETWORK = 'ERR_NETWORK'
export const AXIOS_ERR_TIMEOUT = 'ECONNABORTED'
export const AXIOS_ERR_TIMEDOUT = 'ETIMEDOUT'
export const AXIOS_CODE_BAD_REQ = 400
export const AXIOS_CODE_NOT_ACCEPT = 406
export const AXIOS_CODE_FORRBIDDEN = 403
export const AXIOS_CODE_UNPROCESSABLE = 422
export const AXIOS_CODE_NOT_FOUND = 404
export const AXIOS_CODE_TOO_MANY_REQUESTS = 429

// The request may have reached the server even though no response came back, so the
// outcome (e.g. a payment) is unknown — a retry is not guaranteed to be safe.
export function isAmbiguousTransportError(error: AxiosError | undefined): boolean {
  return [AXIOS_ERR_NETWORK, AXIOS_ERR_TIMEOUT, AXIOS_ERR_TIMEDOUT].includes(error?.code || '')
}

//add own prop to config
declare module 'axios' {
  export interface AxiosRequestConfig {
    panelApi?: boolean
    skipChannel?: boolean
  }
}

// API host is derived from the current domain at runtime so that a single build
// can serve multiple country front-ends (xyz.pl, xyz.fr, xyz.de, ...), each talking
// to its own same-site API sibling (api.xyz.pl, api.xyz.fr, ...). This keeps the
// session cookie first-party (same-site) instead of a blocked third-party cookie.
// REACT_APP_API_URL still wins when set (local dev / explicit per-env override).
const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_API_URL
  if (envUrl && envUrl.length > 0) return envUrl
  const { protocol, host } = window.location
  // strip leading www. so www.xyz.pl and xyz.pl both map to api.xyz.pl
  const apiHost = host.replace(/^www\./, '')
  return `${protocol}//api.${apiHost}`
}

const axios = axiosLib.create({
  baseURL: getApiBaseUrl(),
  timeout: 120000,
  withCredentials: true,
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    'Content-Type': 'application/json',
  },
})

const encodePram = (param: string) => {
  //prevent double encoding
  return encodeURIComponent(decodeURIComponent(param))
}

axios.defaults.paramsSerializer = params => {
  let result = ''
  Object.keys(params).forEach(key => {
    result += `${key}=${encodePram(params[key])}&`
  })
  return result.substr(0, result.length - 1)
}

axios.interceptors.request.use(config => {
  //console.log({ config }, process.env.REACT_APP_API_URL)
  const currentChannel = store.getState().global?.channels?.current!
  if (!config.params) config.params = {}
  if (currentChannel && !config.skipChannel) config.params.channel = currentChannel?.id!
  if (config.panelApi) {
    config.baseURL = process.env.REACT_APP_PANEL_API_URL
    config.headers!['x-pb-api-key'] = process.env.REACT_APP_PANEL_BACKEND_API_KEY
  }
  return config
})

axios.interceptors.response.use(
  response => {
    return response
  },
  error => {
    if (_.includes([403], _.get(error, 'response.status', ''))) {
      console.log('UNAUTH ' + error.response.status)
      return Promise.resolve(error)
    }

    return Promise.reject(error)
  },
)

export default axios
